import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { ExpenseReportService } from "services/expense-report";
import { getPrintTemplate, numberToWords } from "./components/printTemplate";

const createRow = () => ({
  serialNumber: "",
  voucherNumber: "",
  voucherDate: "",
  expenseContent: "",
  amount: "",
});

const splitDateParts = (dateText = "") => {
  const [day = "", month = "", year = ""] = String(dateText).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const composeDate = (parts, label) => {
  if (!parts.day || !parts.month || !parts.year) {
    throw new Error(`${label} chưa nhập đủ ngày/tháng/năm`);
  }
  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);
  const maxDay = new Date(year, month, 0).getDate();

  if (month < 1 || month > 12 || year < 1900 || year > 9999 || day < 1 || day > maxDay) {
    throw new Error(`${label} không hợp lệ`);
  }

  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
};

const validateVoucherDate = (value, label) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const parts = splitDateParts(text);
  return composeDate(parts, label);
};

export default function ExpenseReportDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const today = new Date();
  const [reportDateParts, setReportDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });

  const [header, setHeader] = useState({
    unit: "",
    department: "",
    payerName: "",
    payerDepartmentAddress: "",
    workDescription: "",
    attachedDocuments: "",
  });

  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await ExpenseReportService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          department: data.department || "",
          payerName: data.payerName || "",
          payerDepartmentAddress: data.payerDepartmentAddress || "",
          workDescription: data.workDescription || "",
          attachedDocuments: data.attachedDocuments || "",
        });

        if (data.reportDate) {
          setReportDateParts(splitDateParts(data.reportDate));
        }

        const mappedRows = (data.rows || []).map((row, index) => ({
          serialNumber: String(index + 1),
          voucherNumber: row.voucherNumber || "",
          voucherDate: row.voucherDate || "",
          expenseContent: row.expenseContent || "",
          amount: formatVndIntegerInput(row.amount || ""),
        }));

        setRows(mappedRows.length ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng kê chi tiền");
      }
    })();
  }, [id]);

  const totals = useMemo(
    () => ({
      amount: rows.reduce((acc, row) => acc + parseVndIntegerInput(row.amount), 0),
    }),
    [rows]
  );

  const totalAmountInWords = useMemo(() => numberToWords(totals.amount), [totals.amount]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setReportDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setReportDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setReportDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const updateRow = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      const current = { ...next[index] };
      if (field === "amount") {
        current.amount = formatVndIntegerInput(value);
      } else {
        current[field] = value;
      }
      next[index] = current;
      return next;
    });
  };

  const addRow = () => setRows((prev) => [...prev, createRow()]);

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const payerName = String(header.payerName || "").trim();
    const payerDepartmentAddress = String(header.payerDepartmentAddress || "").trim();
    const workDescription = String(header.workDescription || "").trim();
    const attachedDocuments = String(header.attachedDocuments || "").trim();
    const reportDate = composeDate(reportDateParts, "Ngày bảng kê");

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!department) throw new Error("Bộ phận không được để trống");
    if (!payerName) throw new Error("Họ và tên người chi không được để trống");
    if (!payerDepartmentAddress) throw new Error("Bộ phận (hoặc địa chỉ) không được để trống");
    if (!workDescription) throw new Error("Chi cho công việc không được để trống");

    const normalizedRows = rows
      .map((row, index) => {
        const voucherNumber = String(row.voucherNumber || "").trim();
        const voucherDate = validateVoucherDate(row.voucherDate, `Dòng ${index + 1}: Ngày, tháng`);
        const expenseContent = String(row.expenseContent || "").trim();
        const amount = parseVndIntegerInput(row.amount);

        const hasAnyData = voucherNumber || voucherDate || expenseContent || amount > 0;
        if (!hasAnyData) return null;

        if (!expenseContent) throw new Error(`Dòng ${index + 1}: Nội dung chi không được để trống`);
        if (amount <= 0) throw new Error(`Dòng ${index + 1}: Số tiền phải lớn hơn 0`);

        return {
          serialNumber: String(index + 1),
          voucherNumber,
          voucherDate,
          expenseContent,
          amount,
        };
      })
      .filter(Boolean);

    if (!normalizedRows.length) throw new Error("Bảng kê chi tiền phải có ít nhất 1 dòng dữ liệu");

    return {
      unit,
      department,
      reportDate,
      payerName,
      payerDepartmentAddress,
      workDescription,
      amountInWords: totalAmountInWords,
      attachedDocuments,
      rows: normalizedRows,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await ExpenseReportService.create(payload);
        toast.success("Tạo bảng kê chi tiền thành công");
      } else {
        await ExpenseReportService.update(id, payload);
        toast.success("Cập nhật bảng kê chi tiền thành công");
      }
      navigation("/templates/expense-report");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const printableRows = rows
      .map((row, index) => ({
        serialNumber: String(index + 1),
        voucherNumber: String(row.voucherNumber || "").trim(),
        voucherDate: String(row.voucherDate || "").trim(),
        expenseContent: String(row.expenseContent || "").trim(),
        amount: parseVndIntegerInput(row.amount),
      }))
      .filter((row) => row.voucherNumber || row.voucherDate || row.expenseContent || row.amount > 0);

    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        reportDate: `${String(reportDateParts.day || "").padStart(2, "0")}/${String(reportDateParts.month || "").padStart(2, "0")}/${reportDateParts.year || ""}`,
      },
      rows: printableRows,
      totalAmount: totals.amount,
      totalAmountInWords,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputCenter: {
      textAlign: "center",
    },
    inputRight: {
      textAlign: "right",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: 10,
    },
    th: {
      border: "1px solid #000",
      padding: "6px 4px",
      fontWeight: 700,
      textAlign: "center",
      fontSize: "15px",
    },
    td: {
      border: "1px solid #000",
      padding: "4px 6px",
      fontSize: "15px",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card style={s.container}>
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                  <SoftBox>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.unit}
                        disabled={containView}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <b>Bộ phận:</b>{" "}
                      <input
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.department}
                        disabled={containView}
                        onChange={(event) => setHeaderField("department", event.target.value)}
                      />
                    </div>
                  </SoftBox>

                  <SoftBox textAlign="right">
                    <div><b>Mẫu số 09 - TT</b></div>
                    <div style={{ fontStyle: "italic", fontSize: 14 }}>(Kèm theo Thông tư số 99/2025/TT-BTC</div>
                    <div style={{ fontStyle: "italic", fontSize: 14 }}>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
                  </SoftBox>
                </SoftBox>

                <div style={{ textAlign: "center", marginTop: 10 }}>
                  <div style={{ fontSize: 28, fontWeight: 700 }}>BẢNG KÊ CHI TIỀN</div>
                  <div style={{ marginTop: 6, fontStyle: "italic" }}>
                    Ngày{" "}
                    <input
                      style={{ ...s.inputDotted, width: 34, ...s.inputCenter }}
                      value={reportDateParts.day}
                      disabled={containView}
                      onChange={(event) => setReportDateField("day", event.target.value)}
                      onBlur={() => padDateField("day")}
                    />{" "}
                    tháng{" "}
                    <input
                      style={{ ...s.inputDotted, width: 34, ...s.inputCenter }}
                      value={reportDateParts.month}
                      disabled={containView}
                      onChange={(event) => setReportDateField("month", event.target.value)}
                      onBlur={() => padDateField("month")}
                    />{" "}
                    năm{" "}
                    <input
                      style={{ ...s.inputDotted, width: 60, ...s.inputCenter }}
                      value={reportDateParts.year}
                      disabled={containView}
                      onChange={(event) => setReportDateField("year", event.target.value)}
                    />
                  </div>
                </div>

                <SoftBox mt={2}>
                  <div className="line-field">
                    Họ và tên người chi:{" "}
                    <input
                      style={{ ...s.inputDotted, width: 320 }}
                      value={header.payerName}
                      disabled={containView}
                      onChange={(event) => setHeaderField("payerName", event.target.value)}
                    />
                  </div>
                  <div className="line-field" style={{ marginTop: 6 }}>
                    Bộ phận (hoặc địa chỉ):{" "}
                    <input
                      style={{ ...s.inputDotted, width: 300 }}
                      value={header.payerDepartmentAddress}
                      disabled={containView}
                      onChange={(event) => setHeaderField("payerDepartmentAddress", event.target.value)}
                    />
                  </div>
                  <div className="line-field" style={{ marginTop: 6 }}>
                    Chi cho công việc:{" "}
                    <input
                      style={{ ...s.inputDotted, width: 360 }}
                      value={header.workDescription}
                      disabled={containView}
                      onChange={(event) => setHeaderField("workDescription", event.target.value)}
                    />
                  </div>
                </SoftBox>

                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th, width: "10%" }} rowSpan={2}>STT</th>
                      <th style={{ ...s.th, width: "34%" }} colSpan={2}>Chứng từ</th>
                      <th style={{ ...s.th, width: "36%" }} rowSpan={2}>Nội dung chi</th>
                      <th style={{ ...s.th, width: "20%" }} rowSpan={2}>Số tiền</th>
                      {!containView && <th style={{ ...s.th, width: "4%" }} rowSpan={2}>#</th>}
                    </tr>
                    <tr>
                      <th style={s.th}>Số hiệu</th>
                      <th style={s.th}>Ngày, tháng</th>
                    </tr>
                    <tr>
                      <th style={s.th}>A</th>
                      <th style={s.th}>B</th>
                      <th style={s.th}>C</th>
                      <th style={s.th}>D</th>
                      <th style={s.th}>1</th>
                      {!containView && <th style={s.th}>#</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={`row-${index}`}>
                        <td style={{ ...s.td, textAlign: "center" }}>{index + 1}</td>
                        <td style={s.td}>
                          <input
                            style={{ ...s.inputDotted, width: "100%", ...s.inputCenter }}
                            value={row.voucherNumber}
                            disabled={containView}
                            onChange={(event) => updateRow(index, "voucherNumber", event.target.value)}
                          />
                        </td>
                        <td style={s.td}>
                          <input
                            style={{ ...s.inputDotted, width: "100%", ...s.inputCenter }}
                            value={row.voucherDate}
                            disabled={containView}
                            onChange={(event) => updateRow(index, "voucherDate", event.target.value)}
                            placeholder="dd/mm/yyyy"
                          />
                        </td>
                        <td style={s.td}>
                          <input
                            style={{ ...s.inputDotted, width: "100%" }}
                            value={row.expenseContent}
                            disabled={containView}
                            onChange={(event) => updateRow(index, "expenseContent", event.target.value)}
                          />
                        </td>
                        <td style={{ ...s.td, textAlign: "right" }}>
                          <input
                            style={{ ...s.inputDotted, width: "100%", ...s.inputRight }}
                            value={row.amount}
                            disabled={containView}
                            onChange={(event) => updateRow(index, "amount", event.target.value)}
                          />
                        </td>
                        {!containView && (
                          <td style={{ ...s.td, textAlign: "center", color: "red", cursor: "pointer" }} onClick={() => removeRow(index)}>
                            x
                          </td>
                        )}
                      </tr>
                    ))}
                    <tr>
                      <td style={s.td}></td>
                      <td style={s.td}></td>
                      <td style={s.td}></td>
                      <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                      <td style={{ ...s.td, textAlign: "right", fontWeight: 700 }}>{formatVndIntegerInput(totals.amount)}</td>
                      {!containView && <td style={s.td}></td>}
                    </tr>
                  </tbody>
                </table>

                <SoftBox mt={1}>Số tiền bằng chữ: <strong>{totalAmountInWords}</strong></SoftBox>
                <SoftBox mt={1}>
                  (Kèm theo{" "}
                  <input
                    style={{ ...s.inputDotted, width: 80, ...s.inputCenter }}
                    value={header.attachedDocuments}
                    disabled={containView}
                    onChange={(event) => setHeaderField("attachedDocuments", event.target.value)}
                  />{" "}
                  chứng từ gốc).
                </SoftBox>

                <SoftBox mt={2} display="flex" justifyContent="flex-end">
                  {!containView && (
                    <SoftButton variant="outlined" color="dark" onClick={addRow}>
                      THÊM DÒNG
                    </SoftButton>
                  )}
                </SoftBox>

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="outlined" color="secondary" onClick={() => navigation("/templates/expense-report")}>TRỞ VỀ</SoftButton>
                  <SoftBox display="flex" gap={1}>
                    {containView && (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    )}
                    {!containView && (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {loadingSubmit ? "Đang lưu..." : id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
